import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  ButtonLink,
  Heading,
  Hidden,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';
import type { ComponentProps, FC, ReactElement } from 'react';

import type { TrackingData } from 'src/components/app/FilterSideBar/useFilters';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { EmptySearchIconLarge } from 'src/shared/icons/EmptySearchIcon/EmptySearchIconLarge';
import { EmptySearchIconMedium } from 'src/shared/icons/EmptySearchIcon/EmptySearchIconMedium';
import { EmptySearchIconSmall } from 'src/shared/icons/EmptySearchIcon/EmptySearchIconSmall';
import { MoveIconLarge } from 'src/shared/icons/MoveIcon/MoveIconLarge';
import { MoveIconMedium } from 'src/shared/icons/MoveIcon/MoveIconMedium';
import { MoveIconSmall } from 'src/shared/icons/MoveIcon/MoveIconSmall';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import { trackCandidateManagementTalentSearchViewRecommendedCandidatesBtnPressed } from 'src/utils/events';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';

import translations from './.vocab';
import { EmptyInboxIcon } from './EmptyInboxIcon';

interface EmptyStateLayoutProps {
  icon: ReactElement;
  messageHeading: string;
  children?: ComponentProps<typeof Stack>['children'];
}
const EmptyStateLayout: FC<EmptyStateLayoutProps> = ({
  icon,
  children,
  messageHeading,
}) => (
  <Box background="surface" paddingY={['none', 'none', 'xsmall']}>
    <Box
      background="surface"
      paddingX={['gutter', 'xxlarge', 'xxxlarge']}
      paddingY={['large', 'xlarge']}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Stack space="large" align="center">
        {icon}
        <Stack space={['medium', 'medium', 'gutter']} align="center">
          <Hidden above="tablet">
            <Text size="standard" weight="medium" align="center" component="h4">
              {messageHeading}
            </Text>
          </Hidden>
          <Hidden below="desktop">
            <Heading level="4" align="center">
              {messageHeading}
            </Heading>
          </Hidden>
          {children}
        </Stack>
      </Stack>
    </Box>
  </Box>
);

const responsiveMoveIcon = (
  <Box>
    <Box display={{ mobile: 'block', tablet: 'none', desktop: 'none' }}>
      <MoveIconSmall />
    </Box>
    <Box display={{ mobile: 'none', tablet: 'block', desktop: 'none' }}>
      <MoveIconMedium />
    </Box>
    <Box display={{ mobile: 'none', tablet: 'none', desktop: 'block' }}>
      <MoveIconLarge />
    </Box>
  </Box>
);
const responsiveEmptySearchIcon = (
  <Box>
    <Box display={{ mobile: 'block', tablet: 'none', desktop: 'none' }}>
      <EmptySearchIconSmall />
    </Box>
    <Box display={{ mobile: 'none', tablet: 'block', desktop: 'none' }}>
      <EmptySearchIconMedium />
    </Box>
    <Box display={{ mobile: 'none', tablet: 'none', desktop: 'block' }}>
      <EmptySearchIconLarge />
    </Box>
  </Box>
);
interface BucketEmptyStateWithRecommendedCandidateProps {
  isActive: boolean;
  hasNoCandidates: boolean;
  jobId: number;
  bucket: BUCKETS;
}
const BucketEmptyStateWithRecommendedCandidates: FC<
  BucketEmptyStateWithRecommendedCandidateProps
> = ({ hasNoCandidates, isActive, jobId, bucket }) => {
  if (bucket === BUCKETS.UNKNOWN) throw new Error('Unknown bucket name');

  if (isActive) {
    return (
      <ActiveJobEmptyState
        hasNoCandidates={hasNoCandidates}
        jobId={jobId}
        bucket={bucket}
      />
    );
  }

  return <InactiveJobBucketEmptyState bucket={bucket} />;
};

interface ActiveJobEmptyStateProps {
  hasNoCandidates: boolean;
  jobId: number;
  bucket: BUCKETS;
}
const ActiveJobEmptyState: FC<ActiveJobEmptyStateProps> = ({
  hasNoCandidates,
  jobId,
  bucket,
}) => {
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();
  const { getLabelFromBucket } = useBucketLabel();
  const [, setQueryQueryParams] = useQueryParamsStore();

  if (hasNoCandidates) {
    return (
      <Box paddingX="medium">
        <EmptyStateLayout
          icon={responsiveEmptySearchIcon}
          messageHeading={t('No applications yet')}
        >
          <Hidden above="tablet">
            <Text size="small" tone="secondary" align="center">
              {t(
                "While you're waiting for applications, check out candidates we've found who are a good match for your job. View their CVs and invite them to apply to your job ad.",
              )}
            </Text>
          </Hidden>
          <Hidden below="desktop">
            <Text size="standard" tone="secondary" align="center">
              {t(
                "While you're waiting for applications, check out candidates we've found who are a good match for your job. View their CVs and invite them to apply to your job ad.",
              )}
            </Text>
          </Hidden>
          <Box>
            <ButtonLink
              role="button"
              size="small"
              href={router.recommendedCandidates({ jobId })}
              onClick={() =>
                trackCandidateManagementTalentSearchViewRecommendedCandidatesBtnPressed()
              }
            >
              {t('View recommended candidates')}
            </ButtonLink>
          </Box>
        </EmptyStateLayout>
      </Box>
    );
  }

  if (bucket === BUCKETS.INBOX) {
    const textDescription = (
      <>
        {t('Look in your other folders to see your applications.')}
        <br />
        {t(
          'Need more options? Check out candidates on SEEK who are a good match for your job.',
        )}
      </>
    );
    return (
      <EmptyStateLayout
        icon={responsiveMoveIcon}
        messageHeading={t("You've moved applications out of bucketLabel", {
          bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
        })}
      >
        <Hidden above="tablet">
          <Text size="small" tone="secondary" align="center">
            {textDescription}
          </Text>
        </Hidden>
        <Hidden below="desktop">
          <Text size="standard" tone="secondary" align="center">
            {textDescription}
          </Text>
        </Hidden>
        <Box>
          <ButtonLink
            role="button"
            href={router.recommendedCandidates({ jobId })}
            onClick={() => {}}
            size="small"
          >
            {t('View recommended candidates')}
          </ButtonLink>
        </Box>
      </EmptyStateLayout>
    );
  }

  return (
    <EmptyStateLayout
      icon={responsiveMoveIcon}
      messageHeading={t("You've moved applications out of bucketLabel", {
        bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
      })}
    >
      <Hidden above="tablet">
        <Text size="small" tone="secondary" align="center">
          {t('Look in your other folders to see your applications.')}
        </Text>
      </Hidden>
      <Hidden below="desktop">
        <Text size="standard" tone="secondary" align="center">
          {t('Look in your other folders to see your applications.')}
        </Text>
      </Hidden>
      <Box>
        <Button
          onClick={() => setQueryQueryParams.navigateToBucket(BUCKETS.INBOX)}
          size="small"
        >
          {t('Go to inbox')}
        </Button>
      </Box>
    </EmptyStateLayout>
  );
};

const InactiveJobBucketEmptyState: FC<{
  bucket: BUCKETS;
}> = ({ bucket }) => {
  const { t } = useTranslations(translations);
  const [, setQueryQueryParams] = useQueryParamsStore();
  const { getLabelFromBucket } = useBucketLabel();

  if (bucket === BUCKETS.INBOX) {
    return (
      <EmptyStateLayout
        icon={<EmptyInboxIcon />}
        messageHeading={t('There are no applications in your inbox')}
      />
    );
  }

  return (
    <EmptyStateLayout
      icon={responsiveMoveIcon}
      messageHeading={t('No candidates have been progressed to bucketLabel', {
        bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
      })}
    >
      <Hidden above="tablet">
        <Text size="small" tone="secondary" align="center">
          {t('Progress candidates to see them here.')}
        </Text>
      </Hidden>
      <Hidden below="desktop">
        <Text size="standard" tone="secondary" align="center">
          {t('Progress candidates to see them here.')}
        </Text>
      </Hidden>
      <Box>
        <Button
          onClick={() => setQueryQueryParams.navigateToBucket(BUCKETS.INBOX)}
          size="small"
        >
          {t('Return to inbox')}
        </Button>
      </Box>
    </EmptyStateLayout>
  );
};

interface SearchEmptyStateProps {
  searchText: string;
}
const SearchEmptyState: FC<SearchEmptyStateProps> = ({ searchText }) => {
  const { t } = useTranslations(translations);

  return (
    <EmptyStateLayout
      icon={responsiveEmptySearchIcon}
      messageHeading={t('0 search results for searchText', { searchText })}
    />
  );
};

interface FiltersEmptyStateProps {
  bucket: BUCKETS;
  currentPreFilterTotalOfJobApplications: number;
  resetFilters: (trackingData: TrackingData) => void;
  isMustHaveToggleSelected: boolean;
}
const FiltersEmptyState: FC<FiltersEmptyStateProps> = ({
  bucket,
  currentPreFilterTotalOfJobApplications,
  resetFilters,
  isMustHaveToggleSelected,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  if (isMustHaveToggleSelected) {
    return (
      <EmptyStateLayout
        icon={responsiveEmptySearchIcon}
        messageHeading={t(
          'There are no applications that match your filter selection',
        )}
      >
        <Hidden above="tablet">
          <Stack space="small">
            <Text size="small" tone="secondary" align="center">
              {t(
                'applicationCount application/s in your bucketLabel do not meet your must-have requirements.',
                {
                  bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
                  applicationCount: currentPreFilterTotalOfJobApplications,
                },
              )}
            </Text>
            <Text size="small" tone="secondary" align="center">
              {t('Turn off the must-have filter to view these application/s.', {
                Strong: (children) => <Strong>{children}</Strong>,
                applicationCount: currentPreFilterTotalOfJobApplications,
              })}
            </Text>
          </Stack>
        </Hidden>
        <Hidden below="desktop">
          <Stack space="small">
            <Text tone="secondary" align="center">
              {t(
                'applicationCount application/s in your bucketLabel do not meet your must-have requirements.',
                {
                  bucketLabel: getLabelFromBucket(bucket).toLocaleLowerCase(),
                  applicationCount: currentPreFilterTotalOfJobApplications,
                },
              )}
            </Text>
            <Text tone="secondary" align="center">
              {t('Turn off the must-have filter to view these application/s.', {
                Strong: (children) => <Strong>{children}</Strong>,
                applicationCount: currentPreFilterTotalOfJobApplications,
              })}
            </Text>
          </Stack>
        </Hidden>
        <Box>
          <Button
            onClick={() =>
              resetFilters({
                eventName: 'clear_filters_pressed',
                actionOrigin: 'candidate listing - centre cta with must have',
              })
            }
          >
            {t('Clear all filters')}
          </Button>
        </Box>
      </EmptyStateLayout>
    );
  }

  return (
    <EmptyStateLayout
      icon={responsiveEmptySearchIcon}
      messageHeading={t(
        'There are no applications that match your filter selection',
      )}
    >
      <Hidden above="tablet">
        <Text size="small" tone="secondary" align="center">
          {t('Change or remove filters to broaden your results.')}
        </Text>
      </Hidden>
      <Hidden below="desktop">
        <Text tone="secondary" align="center">
          {t('Change or remove filters to broaden your results.')}
        </Text>
      </Hidden>
      <Box>
        <Button
          onClick={() =>
            resetFilters({
              eventName: 'clear_filters_pressed',
              actionOrigin: 'candidate listing - centre cta without must have',
            })
          }
        >
          {t('Clear all filters')}
        </Button>
      </Box>
    </EmptyStateLayout>
  );
};

interface EmptyStateProps {
  bucket: BUCKETS;
  currentPreFilterTotalOfJobApplications: number;
  isMustHaveToggleSelected: boolean;
  searchText: string;
  resetFilters: (trackingData: TrackingData) => void;
  job: {
    jobId: number;
    isActive: boolean;
    hasNoCandidates: boolean;
  };
}
export const EmptyState: FC<EmptyStateProps> = ({
  bucket,
  currentPreFilterTotalOfJobApplications,
  isMustHaveToggleSelected,
  searchText,
  resetFilters,
  job,
}) => {
  if (searchText !== '') {
    return <SearchEmptyState searchText={searchText} />;
  }

  if (currentPreFilterTotalOfJobApplications === 0) {
    return (
      <BucketEmptyStateWithRecommendedCandidates
        isActive={job.isActive}
        hasNoCandidates={job.hasNoCandidates}
        jobId={job.jobId}
        bucket={bucket}
      />
    );
  }

  return (
    <FiltersEmptyState
      currentPreFilterTotalOfJobApplications={
        currentPreFilterTotalOfJobApplications
      }
      isMustHaveToggleSelected={isMustHaveToggleSelected}
      resetFilters={resetFilters}
      bucket={bucket}
    />
  );
};
